// Custom notification system for the player
// Designed to work reliably in iframe environments

class CustomNotifications {
    constructor() {
        this.container = null;
        this.notifications = new Map();
        this.queue = [];
        this.isProcessingQueue = false;
        this.maxNotifications = 1; // Only show one notification at a time
        this.init();
    }

    init() {
        // Create the notification container
        this.container = document.createElement('div');
        this.container.id = 'custom-notifications-container';
        this.container.style.cssText = `
            position: fixed;
            top: 60px;
            left: 20px;
            width: 175px;
            max-width: calc(100vw - 40px);
            z-index: 2147483647;
            pointer-events: none;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;
        
        document.body.appendChild(this.container);
        
        // Add CSS styles
        this.addStyles();
    }

    addStyles() {
        const style = document.createElement('style');
        style.textContent = `
            .custom-notification {
                background: white;
                border: 1px solid #ddd;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                margin-bottom: 0;
                padding: 12px 16px;
                pointer-events: auto;
                position: relative;
                opacity: 0;
                transform: translateY(-20px);
                transition: all 0.3s ease;
                display: flex;
                align-items: flex-start;
                gap: 10px;
                max-width: 100%;
                word-wrap: break-word;
                font-size: 13px;
            }

            .custom-notification.show {
                opacity: 1;
                transform: translateY(0);
            }

            .custom-notification.info {
                border-left: 4px solid #2196F3;
            }

            .custom-notification.success {
                border-left: 4px solid #4CAF50;
            }

            .custom-notification.warning {
                border-left: 4px solid #FF9800;
            }

            .custom-notification.error {
                border-left: 4px solid #f44336;
            }

            .custom-notification-icon {
                font-size: 20px;
                line-height: 1;
                margin-top: 2px;
                flex-shrink: 0;
            }

            .custom-notification-content {
                flex: 1;
                min-width: 0;
            }

            .custom-notification-title {
                font-weight: 600;
                font-size: 14px;
                margin: 0 0 4px 0;
                color: #333;
            }

            .custom-notification-message {
                font-size: 13px;
                margin: 0;
                color: #666;
                line-height: 1.4;
            }

            .custom-notification-close {
                position: absolute;
                top: 8px;
                right: 8px;
                background: none;
                border: none;
                font-size: 18px;
                cursor: pointer;
                color: #999;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                transition: background-color 0.2s;
            }

            .custom-notification-close:hover {
                background-color: #f5f5f5;
                color: #666;
            }

            .custom-notification-progress {
                position: absolute;
                bottom: 0;
                left: 0;
                height: 3px;
                background: linear-gradient(90deg, #2196F3, #4CAF50);
                border-radius: 0 0 8px 8px;
                transition: width linear;
            }
        `;
        
        document.head.appendChild(style);
    }

    show(message, type = 'info', title = '', duration = 3000) {
        // Add to queue instead of showing immediately
        this.queue.push({
            message,
            type,
            title,
            duration,
            timestamp: Date.now()
        });

        // Process queue if not already processing
        if (!this.isProcessingQueue) {
            this.processQueue();
        }

        return Date.now() + Math.random();
    }

    processQueue() {
        if (this.isProcessingQueue || this.queue.length === 0) {
            return;
        }

        this.isProcessingQueue = true;

        // If there's already a notification showing, remove it first
        if (this.notifications.size > 0) {
            const existingId = this.notifications.keys().next().value;
            this.remove(existingId);
            
            // Wait for removal animation to complete
            setTimeout(() => {
                this.showNextInQueue();
            }, 350);
        } else {
            this.showNextInQueue();
        }
    }

    showNextInQueue() {
        if (this.queue.length === 0) {
            this.isProcessingQueue = false;
            return;
        }

        const notificationData = this.queue.shift();
        const id = Date.now() + Math.random();
        
        // Create and show the notification
        const notification = this.createNotification(id, notificationData.message, notificationData.type, notificationData.title, notificationData.duration);
        
        this.container.appendChild(notification);
        this.notifications.set(id, {
            element: notification,
            timeout: null
        });

        // Animate in
        requestAnimationFrame(() => {
            notification.classList.add('show');
        });

        // Auto-remove after duration
        if (notificationData.duration > 0) {
            const timeout = setTimeout(() => {
                this.remove(id);
            }, notificationData.duration);
            
            this.notifications.get(id).timeout = timeout;
            
            // Add progress bar animation
            const progress = notification.querySelector('.custom-notification-progress');
            if (progress) {
                progress.style.width = '100%';
                progress.style.transitionDuration = `${notificationData.duration}ms`;
                setTimeout(() => {
                    progress.style.width = '0%';
                }, 10);
            }
        }
    }

    createNotification(id, message, type, title, duration) {
        const notification = document.createElement('div');
        notification.className = `custom-notification ${type}`;
        notification.dataset.id = id;

        const icons = {
            info: 'ℹ️',
            success: '✅',
            warning: '⚠️',
            error: '❌'
        };

        notification.innerHTML = `
            <div class="custom-notification-icon">${icons[type] || icons.info}</div>
            <div class="custom-notification-content">
                ${title ? `<div class="custom-notification-title">${title}</div>` : ''}
                <div class="custom-notification-message">${message}</div>
            </div>
            <button class="custom-notification-close" onclick="customNotifications.remove(${id})">×</button>
            ${duration > 0 ? '<div class="custom-notification-progress"></div>' : ''}
        `;

        return notification;
    }

    remove(id) {
        const notificationData = this.notifications.get(id);
        if (!notificationData) return;

        const notification = notificationData.element;
        
        // Clear timeout if exists
        if (notificationData.timeout) {
            clearTimeout(notificationData.timeout);
        }

        // Animate out
        notification.classList.remove('show');
        
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
            this.notifications.delete(id);
            
            // Process next in queue after removal
            this.isProcessingQueue = false;
            this.processQueue();
        }, 300);
    }

    clear() {
        // Clear all visible notifications
        this.notifications.forEach((data, id) => {
            this.remove(id);
        });
        
        // Clear the queue
        this.queue = [];
        this.isProcessingQueue = false;
    }

    // Clear only the queue (useful for rapid editing scenarios)
    clearQueue() {
        this.queue = [];
    }

    // Convenience methods
    info(message, title = '', duration = 2000) {
        return this.show(message, 'info', title, duration);
    }

    success(message, title = '', duration = 2000) {
        return this.show(message, 'success', title, duration);
    }

    warning(message, title = '', duration = 3000) {
        return this.show(message, 'warning', title, duration);
    }

    error(message, title = '', duration = 4000) {
        return this.show(message, 'error', title, duration);
    }
}

// Create global instance
const customNotifications = new CustomNotifications();

// Make it globally available
window.customNotifications = customNotifications;

module.exports = customNotifications;



// WEBPACK FOOTER //
// ./app/helpers/custom-notifications.js